import { useState, type FormEvent } from "react";
import { authClient } from "./auth-client.js";
import { AuthCard, AuthField, LockIcon, MailIcon } from "./auth-card.js";
import { passwordResetDestination } from "../lib/invite-link.js";

export function PasswordResetRequest(props: {
  onBack(): void;
}): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    const result = await authClient.requestPasswordReset({
      email: email.trim(),
      redirectTo:
        window.location.origin +
        passwordResetDestination(window.location.search),
    });
    setPending(false);
    setMessage(
      result.error
        ? "The reset request could not complete."
        : "If that account exists, a reset email is on its way.",
    );
  };
  return (
    <AuthCard
      eyebrow="Private account recovery"
      title="Reset your password."
      intro="We will send one private reset link."
    >
      <form onSubmit={(event) => void submit(event)}>
        <AuthField label="Email" icon={<MailIcon />}>
          <input
            autoComplete="email"
            inputMode="email"
            required
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </AuthField>
        <div className="fr-auth-actions">
          <button
            type="submit"
            className="fr-btn"
            data-variant="gold-gradient"
            data-size="lg"
            disabled={pending}
          >
            {pending ? "Please wait…" : "Send reset link"}
          </button>
          <button
            type="button"
            className="fr-btn"
            data-size="block"
            onClick={props.onBack}
          >
            ← Back to sign in
          </button>
        </div>
      </form>
      {message ? (
        <p className="fr-auth-msg" role="status">
          {message}
        </p>
      ) : null}
    </AuthCard>
  );
}

export function PasswordResetCompletion(props: {
  token: string;
}): React.JSX.Element {
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(false);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    const result = await authClient.resetPassword({
      newPassword: password,
      token: props.token,
    });
    setPending(false);
    if (result.error) {
      setMessage("This reset link is invalid or expired.");
      return;
    }
    sessionStorage.setItem(
      "fairway-rivalry:auth-message",
      "Password reset. Sign in with your new password.",
    );
    window.location.assign(passwordResetDestination(window.location.search));
  };
  return (
    <AuthCard
      eyebrow="Private account recovery"
      title="Choose a new password."
      intro="Use at least 10 characters."
    >
      <form onSubmit={(event) => void submit(event)}>
        <AuthField
          label="New password"
          hint="— min. 10 characters"
          icon={<LockIcon />}
        >
          <input
            autoComplete="new-password"
            minLength={10}
            required
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </AuthField>
        <div className="fr-auth-actions">
          <button
            type="submit"
            className="fr-btn"
            data-variant="gold-gradient"
            data-size="lg"
            disabled={pending}
          >
            {pending ? "Please wait…" : "Save new password"}
          </button>
          <a
            className="fr-auth-link"
            href={passwordResetDestination(window.location.search)}
          >
            Back to sign in
          </a>
        </div>
      </form>
      {message ? (
        <p className="fr-auth-msg" data-tone="error" role="alert">
          {message}
        </p>
      ) : null}
    </AuthCard>
  );
}
